import type { AccessibleSupportTeam } from "@redotech/effect-api-shared/rpc/support-ticket-related-records";
import {
  supportTicketFilterData,
  SupportTicketFilterNames,
} from "@redotech/effect-api-shared/schema/support-ticket-filters";
import TeamIcon from "@redotech/redo-icons/building-02.svg";
import { ArrayStringFilterOperator } from "@redotech/redo-views-common/advanced-filters/array-string-filter";
import { AdvancedFilterType } from "@redotech/redo-views-common/advanced-filters/generic-advanced-filter-data";
import type { ArrayStringTableFilter } from "@redotech/redo-web/advanced-filters/advanced-filter";

// Filter values are `accountId`s (the OpenSearch list scope key). This cache
// maps them back to team names for display, populated from the accessible
// teams the filter is built with.
const teamNameCache = new Map<string, string>();

/**
 * Team filter for connected-team merchants: narrows the list to tickets on the
 * selected teams. Values are account ids so the server filter intersects with
 * the existing accountId access scope. Only register this when the caller has
 * more than one accessible team — there's nothing to filter otherwise.
 */
export function createTeamsAdvancedFilter(
  teams: readonly AccessibleSupportTeam[],
): ArrayStringTableFilter<string> {
  for (const team of teams) {
    teamNameCache.set(team.accountId, team.name);
  }
  return {
    type: AdvancedFilterType.ARRAY_TO_STRING,
    Icon: TeamIcon,
    operators: [
      ArrayStringFilterOperator.ANY_OF,
      ArrayStringFilterOperator.NONE_OF,
    ],
    data: supportTicketFilterData[SupportTicketFilterNames.ACCOUNT],
    itemLabel: "team",
    itemLabelPlural: "teams",
    displayName: "Team",
    valueFetcher: async (_params, _signal) =>
      teams.map((team) => team.accountId),
    display: (item: string) => teamNameCache.get(item) ?? item,
  };
}
